import React from 'react';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { AaiContext, WhatsAllowedData } from '../../types/aai';
import { ErrorNote, Loading, PageBody, PageHead } from '../../components/aa';
import type { ActivityLinkResolver } from '../partner/ActivityView';
import { ClientContextBody, useLatestCollection } from '../partner/ClientContextPage';
import { departmentName, useDepartment } from './useDepartment';
import { DepartmentRoster } from './DepartmentRoster';

/*
 * Department Context, for the Department Head.
 *
 * It shares a body with the Owner's Company Context, but it is not the same
 * screen and no longer pretends to be. A head runs one department: what Orbit
 * knows about it, the questions still open on it, its people, and its own
 * activity record. The company's tools and grants, who signs the company's
 * releases and the rules published for everybody are the Owner's record, and
 * none of them render here.
 *
 * The scoping is the server's, not this page's: the Room read model forces a
 * head's purpose to their own department and filters the department list to the
 * one they lead, so another department's people and counts never reach the
 * browser at all.
 */

const departmentLinks: ActivityLinkResolver = (event) => {
  if (event.entity_type === 'request' && event.entity_id) return { label: 'Open project', to: `/projects?open=${event.entity_id}` };
  if (event.entity_type === 'skill') return { label: 'Open Quick Wins', to: '/projects?tab=quick_wins' };
  return null;
};

const DepartmentCompanyContextPage: React.FC = () => {
  const clientId = localStorage.getItem('client_id') || '';
  const clientName = localStorage.getItem('client_name') || 'Your company';

  const context = useAsync<AaiContext>(() => aaiApi.getContext(), 'Could not load your context.');
  const allowed = useAsync<WhatsAllowedData>(() => aaiApi.getWhatsAllowed(), 'Could not load company context.');
  const overview = useDepartment();
  const cycle = useLatestCollection(clientId || undefined);

  if (allowed.loading || overview.loading) return <PageBody><Loading label="Loading company context" /></PageBody>;
  if (allowed.error || !allowed.data) return <PageBody><ErrorNote message={allowed.error || 'Something went wrong.'} /></PageBody>;

  const companyName = context.data?.company_name || clientName;
  const department = departmentName(overview.data);
  const grants = overview.data?.access.source_grants ?? 0;
  const isLead = overview.data?.department.is_lead ?? false;

  return (
    <PageBody>
      <PageHead
        align="center"
        eyebrow={department}
        title="Department context"
        sub="What your department does, the tools it runs on, the people in it, and what Orbit still needs from you."
      />
      <ClientContextBody
        viewer="department"
        clientId={clientId}
        clientName={companyName}
        allowed={allowed.data}
        cycle={cycle}
        links={departmentLinks}
        people={<DepartmentRoster />}
        room={false}
      />
    </PageBody>
  );
};

export default DepartmentCompanyContextPage;
